import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { trackAddToCart } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartLoading, setCartLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) { setCart({ items: [], total: 0 }); return; }
    setCartLoading(true);
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
    } catch {
      setCart({ items: [], total: 0 });
    } finally {
      setCartLoading(false);
    }
  }, [user]);

  const addToCart = async (productId, quantity = 1, variant = null) => {
    if (!user) return { success: false, error: "Please log in to add items to cart" };
    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity, variant }, { withCredentials: true });
      await fetchCart();
      // Look up the freshly-added line to fire analytics with the right price
      try {
        const { data: c } = await axios.get(`${API}/cart`, { withCredentials: true });
        const it = (c.items || []).find(
          (x) => x.product_id === productId && (x.variant || null) === (variant || null)
        );
        if (it) trackAddToCart({ id: it.product_id, name: it.name, price: it.price, quantity, variant });
      } catch { /* analytics is best-effort */ }
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || "Failed to add to cart" };
    }
  };

  const updateQuantity = async (productId, quantity, variant = null) => {
    try {
      await axios.post(`${API}/cart/update`, { product_id: productId, quantity, variant }, { withCredentials: true });
      await fetchCart();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('cart updateQuantity failed:', e?.response?.data || e.message);
    }
  };

  const removeItem = async (productId, variant = null) => {
    try {
      const params = variant ? `?variant=${encodeURIComponent(variant)}` : '';
      await axios.delete(`${API}/cart/remove/${productId}${params}`, { withCredentials: true });
      await fetchCart();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('cart removeItem failed:', e?.response?.data || e.message);
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, { withCredentials: true });
      setCart({ items: [], total: 0 });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('cart clearCart failed:', e?.response?.data || e.message);
    }
  };

  const itemCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, cartLoading, fetchCart, addToCart, updateQuantity, removeItem, clearCart, itemCount }}>
      {children}
    </CartContext.Provider>
  );
}
